"use client";

import React, { ReactNode } from "react";

import Link from "next/link";

import { Button } from "@heroui/button";
import { Card, CardBody } from "@heroui/card";
import {
  AlertCircle,
  CircleSlash,
  FileQuestion,
  FolderX,
  Heart,
  PanelLeftInactive,
  Search,
} from "lucide-react";

import { COMPONENT_SIZES, SPACING } from "@/lib/constants/layoutConstants";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { OrganizationTerminology } from "@/lib/services/terminologyService";
import { cn } from "@/lib/utils";

type EmptyStateVariant =
  | "filtered"
  | "no-data"
  | "error"
  | "search"
  | "folder"
  | "panel"
  | "default";

interface EmptyStateProps {
  /**
   * The message to display in the empty state
   */
  message?: string;
  /**
   * Optional title to display above the message
   */
  title?: string;
  /**
   * Optional action label for the button
   */
  actionLabel?: string;
  /**
   * Optional action href for the button link
   */
  actionHref?: string;
  /**
   * Optional custom action content (overrides actionLabel/actionHref)
   */
  action?: ReactNode;
  /**
   * Optional custom icon to override the default
   */
  icon?: ReactNode;
  /**
   * Visual variant of the empty state
   */
  variant?: EmptyStateVariant;
  /**
   * Organization name for personalized messaging (for needs variant)
   */
  organizationName?: string;
  /**
   * Terminology for language customization (required for needs variants; use getDefaultTerminology() if no org).
   */
  terminology: OrganizationTerminology;
  /**
   * Accessible label for screen readers
   */
  ariaLabel?: string;
  /**
   * Custom aria-label for the action button
   */
  actionAriaLabel?: string;
  /**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Optional test ID for testing
   */
  testId?: string;
  /**
   * Optional ID for the component
   */
  id?: string;
  /**
   * Whether to use card wrapper (default: true for dashboard variant, false for needs variant)
   */
  useCard?: boolean;
}

type NeedsContent = {
  actionLabel: string;
  icon: string;
  message: string;
  title: string;
} | null;

function getNeedsContent(
  variant: EmptyStateVariant,
  terminology: OrganizationTerminology,
  organizationName?: string,
): NeedsContent {
  const needPlural = terminology.need.plural;
  const volunteerPlural = terminology.volunteer.plural;
  const termCommunity = terminology.termCommunity;

  switch (variant) {
    case "no-data":
      return {
        actionLabel: "Browse Other Organizations",
        icon: "heart",
        message: organizationName
          ? `${organizationName} hasn't posted any ${needPlural.toLowerCase()} for ${volunteerPlural.toLowerCase()} yet. Check back soon – this ${termCommunity.toLowerCase()} values your willingness to help!`
          : `This organization hasn't posted any ${needPlural.toLowerCase()} for ${volunteerPlural.toLowerCase()} yet. Check back soon!`,
        title: organizationName
          ? `${organizationName} hasn't posted any ${needPlural.toLowerCase()} yet`
          : `No ${needPlural.toLowerCase()} posted yet`,
      };
    case "error":
      return {
        actionLabel: "Try Again",
        icon: "alert",
        message: `We're having trouble loading the ${needPlural.toLowerCase()} right now. Please try again in a moment.`,
        title: `Unable to load ${needPlural.toLowerCase()}`,
      };
    case "filtered":
      return {
        actionLabel: "Clear Filters",
        icon: "search",
        message: `All ${needPlural.toLowerCase()} in the area have currently been met! Thank you for your willingness to help. Check back soon or try adjusting your filters to see if there are ${needPlural.toLowerCase()} in other areas.`,
        title: `All ${needPlural.toLowerCase()} have been met`,
      };
    default:
      return null;
  }
}

function renderNeedsIcon(iconName: string) {
  switch (iconName) {
    case "heart":
      return (
        <Heart
          aria-hidden="true"
          className={cn("text-primary", COMPONENT_SIZES.ICON_LG)}
        />
      );
    case "alert":
      return (
        <AlertCircle
          aria-hidden="true"
          className={cn("text-danger", COMPONENT_SIZES.ICON_LG)}
        />
      );
    case "search":
      return (
        <Search
          aria-hidden="true"
          className={cn("text-primary", COMPONENT_SIZES.ICON_LG)}
        />
      );
    default:
      return (
        <CircleSlash
          aria-hidden="true"
          className={cn("text-default-400", COMPONENT_SIZES.ICON_LG)}
        />
      );
  }
}

function renderVariantIcon(variant: EmptyStateVariant) {
  switch (variant) {
    case "search":
      return (
        <FileQuestion
          aria-hidden="true"
          className={cn("text-primary", COMPONENT_SIZES.ICON_LG)}
        />
      );
    case "folder":
      return (
        <FolderX
          aria-hidden="true"
          className={cn("text-primary", COMPONENT_SIZES.ICON_LG)}
        />
      );
    case "panel":
      return (
        <PanelLeftInactive
          aria-hidden="true"
          className={cn("text-primary", COMPONENT_SIZES.ICON_LG)}
        />
      );
    default:
      return (
        <CircleSlash
          aria-hidden="true"
          className={cn("text-primary", COMPONENT_SIZES.ICON_LG)}
        />
      );
  }
}

function resolveIcon(
  icon: ReactNode | undefined,
  needsContent: NeedsContent,
  variant: EmptyStateVariant,
): ReactNode {
  if (icon) return icon;
  if (needsContent) return renderNeedsIcon(needsContent.icon);

  return renderVariantIcon(variant);
}

function publishActionEvent(
  actionHref: string,
  finalActionLabel: string | undefined,
  variant: EmptyStateVariant,
) {
  void EventPublisher.publishEvent(EventKeys.EMPTY_STATE_ACTION_BUTTON_CLICK, {
    actionHref,
    actionLabel: finalActionLabel,
    variant,
  });
}

function buildActionButton(
  buttonAriaLabel: string,
  finalActionLabel: string | undefined,
  actionHref: string | undefined,
  variant: EmptyStateVariant,
): ReactNode {
  const button = (
    <Button
      aria-label={buttonAriaLabel}
      color="primary"
      variant="flat"
      onPress={() => {
        if (actionHref)
          publishActionEvent(actionHref, finalActionLabel, variant);
      }}
    >
      {finalActionLabel}
    </Button>
  );

  return actionHref ? <Link href={actionHref}>{button}</Link> : button;
}

function renderActionButton(
  action: ReactNode | undefined,
  actionHref: string | undefined,
  finalActionLabel: string | undefined,
  actionAriaLabel: string | undefined,
  variant: EmptyStateVariant,
): ReactNode {
  if (action) return action;
  if (!actionHref && !finalActionLabel) return null;

  const buttonAriaLabel = actionAriaLabel ?? finalActionLabel ?? "Action";

  return buildActionButton(
    buttonAriaLabel,
    finalActionLabel,
    actionHref,
    variant,
  );
}

interface ResolvedEmptyStateValues {
  componentId: string;
  titleId: string;
  messageId: string;
  needsContent: NeedsContent;
  finalTitle: string | undefined;
  finalMessage: string | undefined;
  finalActionLabel: string | undefined;
  finalAriaLabel: string;
  shouldUseCard: boolean;
}

function isNeedsVariant(variant: EmptyStateVariant): boolean {
  return variant === "filtered" || variant === "no-data" || variant === "error";
}

function resolveNeedsContent(
  variant: EmptyStateVariant,
  terminology: OrganizationTerminology,
  organizationName: string | undefined,
): NeedsContent {
  return isNeedsVariant(variant)
    ? getNeedsContent(variant, terminology, organizationName)
    : null;
}

function resolveFromNeedsContent(
  override: string | undefined,
  needsValue: string | undefined,
): string | undefined {
  return override ?? needsValue;
}

function resolveFinalLabels(
  props: EmptyStateProps,
  needsContent: NeedsContent,
): {
  finalTitle: string | undefined;
  finalMessage: string | undefined;
  finalActionLabel: string | undefined;
  finalAriaLabel: string;
} {
  const finalTitle = resolveFromNeedsContent(props.title, needsContent?.title);
  const finalMessage = resolveFromNeedsContent(
    props.message,
    needsContent?.message,
  );
  const finalActionLabel = resolveFromNeedsContent(
    props.actionLabel,
    needsContent?.actionLabel,
  );
  const finalAriaLabel = props.ariaLabel ?? finalTitle ?? "No data available";

  return { finalActionLabel, finalAriaLabel, finalMessage, finalTitle };
}

function resolveEmptyStateValues(
  props: EmptyStateProps,
  variant: EmptyStateVariant,
): ResolvedEmptyStateValues {
  const componentId = props.id ?? props.testId ?? "empty-state";
  const needsContent = resolveNeedsContent(
    variant,
    props.terminology,
    props.organizationName,
  );
  const labels = resolveFinalLabels(props, needsContent);
  const shouldUseCard =
    props.useCard ?? (variant !== "filtered" && !needsContent);

  return {
    componentId,
    messageId: `${componentId}-message`,
    needsContent,
    shouldUseCard,
    titleId: `${componentId}-title`,
    ...labels,
  };
}

function renderTitleSection(
  finalTitle: string | undefined,
  titleId: string,
  shouldUseCard: boolean,
): ReactNode {
  if (!finalTitle) return null;

  return (
    <h3
      className={cn(
        "font-medium text-default-800 mb-2",
        shouldUseCard ? "text-default-700 mb-1" : "text-xl",
      )}
      id={titleId}
    >
      {finalTitle}
    </h3>
  );
}

function renderMessageSection(
  finalMessage: string | undefined,
  messageId: string,
  shouldUseCard: boolean,
): ReactNode {
  if (!finalMessage) return null;

  return (
    <p
      className={cn(
        "text-default-500 text-center",
        shouldUseCard ? "mb-4" : "max-w-md mb-6",
      )}
      id={messageId}
    >
      {finalMessage}
    </p>
  );
}

function renderActionSection(
  resolvedAction: ReactNode,
  shouldUseCard: boolean,
): ReactNode {
  if (!resolvedAction) return null;

  return (
    <div className={cn(shouldUseCard ? "mt-2" : "")}>{resolvedAction}</div>
  );
}

function renderEmptyStateContent(
  resolved: ResolvedEmptyStateValues,
  resolvedIcon: ReactNode,
  resolvedAction: ReactNode,
): ReactNode {
  const {
    finalAriaLabel,
    finalMessage,
    finalTitle,
    messageId,
    shouldUseCard,
    titleId,
  } = resolved;

  return (
    <div
      aria-label={finalAriaLabel}
      className={cn(
        "flex flex-col items-center justify-center",
        shouldUseCard ? "py-4" : "py-16 px-4",
        SPACING.WITHIN_SECTION,
      )}
      role="region"
    >
      <div
        aria-hidden="true"
        className={cn(
          "mb-4",
          shouldUseCard
            ? "mb-3"
            : "bg-default-50 w-16 h-16 rounded-full flex items-center justify-center",
        )}
      >
        {resolvedIcon}
      </div>

      {renderTitleSection(finalTitle, titleId, shouldUseCard)}
      {renderMessageSection(finalMessage, messageId, shouldUseCard)}
      {renderActionSection(resolvedAction, shouldUseCard)}
    </div>
  );
}

/**
 * Unified empty state component for displaying when no data is available
 * Consolidates needs EmptyState and dashboard EmptyState components
 * Uses org colors for icons and action buttons
 */
export function EmptyState(props: EmptyStateProps) {
  const { variant = "default" } = props;
  const resolved = resolveEmptyStateValues(props, variant);
  const {
    componentId,
    finalMessage,
    finalTitle,
    messageId,
    shouldUseCard,
    titleId,
  } = resolved;

  const resolvedIcon = resolveIcon(props.icon, resolved.needsContent, variant);
  const resolvedAction = renderActionButton(
    props.action,
    props.actionHref,
    resolved.finalActionLabel,
    props.actionAriaLabel,
    variant,
  );

  const content = renderEmptyStateContent(
    resolved,
    resolvedIcon,
    resolvedAction,
  );

  if (shouldUseCard) {
    return (
      <Card
        className={cn("border shadow-sm", props.className)}
        data-testid={componentId}
        id={componentId}
      >
        <CardBody className={cn("text-center", SPACING.CARD_PADDING_MD)}>
          {content}
        </CardBody>
      </Card>
    );
  }

  return (
    <section
      aria-describedby={finalMessage ? messageId : undefined}
      aria-labelledby={finalTitle ? titleId : undefined}
      className={cn("col-span-full", props.className)}
      data-testid={componentId}
      id={componentId}
      role="status"
    >
      {content}
    </section>
  );
}
